import { useState, useEffect } from 'react';
import getServicesForRfv from '~/components/ClinicDetailPageFlexible/components/SelfPay/util/mapRfvToService';
import { ServiceName } from '~/components/ClinicDetailPageFlexible/components/SelfPay/util/Reasons';

/** Categories of services that are non-bookable. All services in the same category share the same text copy in the warning dialog */
export type NonBookableServiceType = 'X-ray' | 'Stitches';

export const nonBookableWarningTitleText: Record<NonBookableServiceType, string> = {
  Stitches: 'A provider can determine if you need stitches during an office visit',
  'X-ray': 'A provider can determine if you need an X-ray during an office visit',
};

export const nonBookableWarningBodyText: Record<NonBookableServiceType, string> = {
  Stitches:
    'Book an office visit to chat with a provider about your concerns. They can determine if you need stitches.',
  'X-ray':
    'Book an office visit to chat with a provider about your concerns. They can determine if you need an X-ray.',
};

/** Determines if a Reason For Visit is a "non-bookable" service offered by the clinic, meaning it needs an additional
 * assessment to determine the kind of care required. Returns the text copy used
 * for an RFV Entry warning dialogue if the service is non-bookable */
const useNonBookableServiceWarning = (reasonForVisit: string) => {
  const [nonBookableServiceType, setNonBookableServiceType] =
    useState<NonBookableServiceType | null>(null);

  useEffect(() => {
    const bestMatchingService = getServicesForRfv(reasonForVisit).services?.[0];

    if (
      bestMatchingService === ServiceName['x-ray_chest'] ||
      bestMatchingService === ServiceName['x-ray_extremity']
    ) {
      setNonBookableServiceType('X-ray');
    } else if (bestMatchingService === ServiceName.laceration_repair) {
      setNonBookableServiceType('Stitches');
    } else {
      setNonBookableServiceType(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reasonForVisit]);

  return {
    type: nonBookableServiceType,
    bodyText: (nonBookableServiceType && nonBookableWarningBodyText[nonBookableServiceType]) ?? '',
    titleText:
      (nonBookableServiceType && nonBookableWarningTitleText[nonBookableServiceType]) ?? '',
  };
};

export default useNonBookableServiceWarning;
